import { useEffect, useId, useRef } from "preact/hooks";

export interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  /** `danger` styles the confirm button for destructive actions. */
  tone?: "default" | "danger";
  /** Disables both buttons while the action is in flight. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Small modal confirmation built on the native `<dialog>` element: `showModal`
 * gives us a focus trap, Escape handling, and top-layer rendering (so no
 * ancestor transform/clip can hide it). Cancel is the first focusable control,
 * so Enter on open cancels rather than confirming.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = "default",
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      class="confirm"
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Intercept the native close so the parent owns the state.
        event.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(event) => {
        // Clicking the backdrop (the dialog element itself) cancels.
        if (event.target === dialogRef.current && !busy) onCancel();
      }}
    >
      <div class="confirm__panel">
        <h2 class="confirm__title" id={titleId}>
          {title}
        </h2>
        <p class="confirm__body">{body}</p>
        <div class="confirm__actions">
          <button
            type="button"
            class="btn btn--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            class={`btn ${tone === "danger" ? "btn--danger" : "btn--primary"}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
